import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import DisableConfirm from "@/components/DisableConfirm";
import { toast } from "sonner";
import { Eye, Pencil, Power, Plus, RotateCcw, Search, Users, Receipt } from "lucide-react";
import LedgerDialog from "@/components/LedgerDialog";

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" }); } catch { return s; }
}

export default function SubscribersList() {
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [pubs, setPubs] = useState([]);
  const [pers, setPers] = useState([]);
  const [orgId, setOrgId] = useState("");
  const [pubId, setPubId] = useState("");
  const [perId, setPerId] = useState("");
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [view, setView] = useState(null);
  const [ledgerSid, setLedgerSid] = useState(null);
  const [disableTarget, setDisableTarget] = useState(null);
  const [disabling, setDisabling] = useState(false);

  useEffect(() => { (async () => { try { const { data } = await api.get("/organizations/active-list"); setOrgs(data); } catch { /* */ } })(); }, []);
  useEffect(() => {
    setPubId(""); setPerId(""); setPubs([]); setPers([]); setData(null);
    if (!orgId) return;
    (async () => { try { const { data } = await api.get("/publication-houses/active-list", { params: { organization_id: orgId } }); setPubs(data); } catch { /* */ } })();
  }, [orgId]);
  useEffect(() => {
    setPerId(""); setPers([]); setData(null);
    if (!pubId) return;
    (async () => {
      try {
        const { data } = await api.get("/periodicals", { params: { publication_house_id: pubId, page: 1, page_size: 100 } });
        setPers(data.items.filter((x) => x.status === "active"));
      } catch { /* */ }
    })();
  }, [pubId]);

  const load = async (p = page) => {
    if (!perId) return;
    setData(null);
    try {
      const { data } = await api.get("/subscribers", { params: { periodical_id: perId, page: p, page_size: 25, search: search || undefined } });
      setData(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); setData({ items: [], total: 0, page: 1, page_size: 25 }); }
  };

  useEffect(() => { setPage(1); load(1); /* eslint-disable-next-line */ }, [perId]);

  const handleDisable = async () => {
    setDisabling(true);
    try {
      await api.patch(`/subscribers/${disableTarget.id}/disable`);
      toast.success("Subscriber disabled"); setDisableTarget(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setDisabling(false); }
  };

  const handleEnable = async (s) => {
    try { await api.patch(`/subscribers/${s.id}/enable`); toast.success("Re-enabled"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
  const newHref = useMemo(() => perId ? `/subscribers/new?periodical_id=${perId}` : "#", [perId]);

  return (
    <div data-testid="subscribers-list-page" className="mx-auto max-w-7xl">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Module 05</div>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">Subscribers</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">Pick a periodical to view subscribers. Server-side paginated for 10k+ records.</p>
        </div>
        <Button asChild disabled={!perId} data-testid="subscriber-create-btn" className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] disabled:opacity-50">
          <Link to={newHref}><Plus size={16} className="mr-1.5" />New Subscriber</Link>
        </Button>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-3 rounded-sm border border-slate-200 bg-white p-4 sm:grid-cols-3">
        <div>
          <Label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Organization</Label>
          <Select value={orgId} onValueChange={setOrgId}>
            <SelectTrigger data-testid="sub-org-select" className="rounded-sm"><SelectValue placeholder="Select organization" /></SelectTrigger>
            <SelectContent>{orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Publication House</Label>
          <Select value={pubId} onValueChange={setPubId} disabled={!orgId}>
            <SelectTrigger data-testid="sub-pub-select" className="rounded-sm"><SelectValue placeholder={orgId ? "Select" : "Pick org first"} /></SelectTrigger>
            <SelectContent>{pubs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Periodical</Label>
          <Select value={perId} onValueChange={setPerId} disabled={!pubId}>
            <SelectTrigger data-testid="sub-per-select" className="rounded-sm"><SelectValue placeholder={pubId ? "Select" : "Pick pub house first"} /></SelectTrigger>
            <SelectContent>{pers.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {!perId ? (
        <div className="rounded-sm border border-dashed border-slate-300 bg-white p-12 text-center text-sm text-slate-500">
          <Users className="mx-auto mb-3 text-slate-400" size={28} />
          Select a periodical above to view its subscribers.
        </div>
      ) : (
      <>
      <form onSubmit={(e) => { e.preventDefault(); load(1); setPage(1); }} className="mb-3 relative max-w-md">
        <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input data-testid="sub-search-input" placeholder="Search code / name / mobile / email…" value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-sm pl-9" />
      </form>
      <div className="rounded-sm border border-slate-200 bg-white">
        <Table data-testid="sub-table" className="data-table">
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Mobile</TableHead>
              <TableHead>Pin · City</TableHead>
              <TableHead>Validity</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data === null && [...Array(5)].map((_, i) => (
              <TableRow key={i}><TableCell colSpan={8}><Skeleton className="h-6 w-full" /></TableCell></TableRow>
            ))}
            {data && data.items.length === 0 && (
              <TableRow><TableCell colSpan={8} className="py-12 text-center text-sm text-slate-500">No subscribers yet.</TableCell></TableRow>
            )}
            {data?.items.map((it) => (
              <TableRow key={it.id} data-testid={`sub-row-${it.id}`}>
                <TableCell className="font-mono text-xs text-slate-700">{it.subscriber_code || "—"}</TableCell>
                <TableCell><div className="font-medium text-slate-900">{it.display_name}</div>{it.email && <div className="text-xs text-slate-500">{it.email}</div>}</TableCell>
                <TableCell><span className={`rounded-sm border px-2 py-0.5 text-xs font-medium ${it.subscriber_type === "Organization" ? "border-amber-200 bg-amber-50 text-amber-800" : "border-indigo-200 bg-indigo-50 text-[#002FA7]"}`}>{it.subscriber_type}</span></TableCell>
                <TableCell className="font-mono text-xs">{it.mobile_no}</TableCell>
                <TableCell className="text-sm text-slate-700"><span className="font-mono text-xs">{it.pincode}</span> · {it.city}</TableCell>
                <TableCell className="text-xs text-slate-600">{fmtDate(it.subscription_start_date)} → {fmtDate(it.subscription_end_date)}</TableCell>
                <TableCell><StatusBadge status={it.status} /></TableCell>
                <TableCell className="text-right whitespace-nowrap" style={{ minWidth: 160 }}>
                  <div className="flex items-center justify-end gap-1">
                    <Button size="icon" variant="ghost" title="View" onClick={() => setView(it)} data-testid={`sub-view-${it.id}`} className="h-8 w-8 rounded-sm text-slate-600 hover:bg-slate-100"><Eye size={16} /></Button>
                    <Button size="icon" variant="ghost" title="Receipts & Ledger" onClick={() => setLedgerSid(it.id)} data-testid={`sub-ledger-${it.id}`} className="h-8 w-8 rounded-sm text-emerald-600 hover:bg-emerald-50"><Receipt size={16} /></Button>
                    <Button size="icon" variant="ghost" title="Edit" onClick={() => navigate(`/subscribers/${it.id}/edit`)} data-testid={`sub-edit-${it.id}`} className="h-8 w-8 rounded-sm text-[#002FA7] hover:bg-indigo-50"><Pencil size={16} /></Button>
                    {it.status !== "disabled" ? (
                      <Button size="icon" variant="ghost" title="Disable" onClick={() => setDisableTarget(it)} data-testid={`sub-disable-${it.id}`} className="h-8 w-8 rounded-sm text-rose-600 hover:bg-rose-50"><Power size={16} /></Button>
                    ) : (
                      <Button size="icon" variant="ghost" title="Re-enable" onClick={() => handleEnable(it)} data-testid={`sub-enable-${it.id}`} className="h-8 w-8 rounded-sm text-emerald-600 hover:bg-emerald-50"><RotateCcw size={16} /></Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {data && data.total > 0 && (
          <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3 text-sm text-slate-600">
            <div>Page <span className="font-medium">{page}</span> of {totalPages} — {data.total} subscriber{data.total !== 1 && "s"}</div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => { setPage(page - 1); load(page - 1); }} className="rounded-sm" data-testid="sub-prev">Prev</Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => { setPage(page + 1); load(page + 1); }} className="rounded-sm" data-testid="sub-next">Next</Button>
            </div>
          </div>
        )}
      </div>
      </>
      )}

      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent data-testid="sub-view-modal" className="max-w-2xl rounded-sm">
          <DialogHeader>
            <DialogTitle>{view?.display_name}</DialogTitle>
            <DialogDescription>{view?.subscriber_code} · {view?.subscriber_type}</DialogDescription>
          </DialogHeader>
          {view && (
            <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
              <Info label="Mobile" value={view.mobile_no} mono />
              <Info label="WhatsApp" value={view.is_whatsapp ? view.mobile_no : view.whatsapp_no} mono />
              <Info label="Email" value={view.email} />
              <Info label="Plan" value={view.plan_name_snapshot} />
              <Info label="Validity" value={`${fmtDate(view.subscription_start_date)} → ${fmtDate(view.subscription_end_date)}`} />
              <Info label="E-Copy" value={view.opt_e_copy ? "Yes" : "No"} />
              <Info label="Source" value={view.source} />
              <Info label="Occasion" value={view.occasion} />
              {view.subscriber_type === "Individual" ? (
                <>
                  <Info label="Gender" value={view.gender} />
                  <Info label="Birthdate" value={view.birthdate} />
                  <Info label="Profession" value={view.profession} />
                </>
              ) : (
                <>
                  <Info label="GST" value={view.gst_number} mono />
                  <Info label="Company Type" value={view.company_type} />
                  <Info label="Contacts" value={(view.contacts || []).map((c) => c.full_name).join(", ")} />
                </>
              )}
              <div className="sm:col-span-2 rounded-sm border border-slate-200 p-3">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Postage Address {view.address_verified && <span className="ml-2 rounded-sm bg-emerald-50 px-1.5 py-0.5 text-[9px] text-emerald-700">VERIFIED</span>}</div>
                <div className="mt-1 text-slate-900">{view.address_line_1}{view.address_line_2 ? ", " + view.address_line_2 : ""}</div>
                <div className="text-slate-600">{view.landmark ? `Near ${view.landmark}, ` : ""}{view.city}{view.district ? `, ${view.district}` : ""} – <span className="font-mono">{view.pincode}</span></div>
                <div className="text-slate-600">{view.state}</div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <DisableConfirm
        open={!!disableTarget}
        onOpenChange={(o) => !o && setDisableTarget(null)}
        onConfirm={handleDisable}
        loading={disabling}
        entityLabel="subscriber"
        warningMessage="Disabling stops physical and e-copy delivery to this subscriber. Soft-deleted; preserved for compliance."
      />

      <LedgerDialog subscriberId={ledgerSid} open={!!ledgerSid} onOpenChange={(o) => !o && setLedgerSid(null)} />
    </div>
  );
}

function Info({ label, value, mono }) {
  return (
    <div className="rounded-sm border border-slate-200 p-3">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-1 text-sm text-slate-900 ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}
